/**
 * Register the RefClick service worker + arm client-side self-heal
 * against the "black screen from a stale cache" failure mode.
 *
 * How the self-heal works:
 *   - A dynamic import (React lazy chunks, code-split assets) that
 *     fails because its hashed filename no longer exists throws a
 *     ChunkLoadError. We catch that (both `window.error` and
 *     `unhandledrejection`) and do ONE:
 *        caches.delete(*) → hard reload.
 *   - A ``sessionStorage`` flag guarantees this happens at most once
 *     per tab so we never loop.
 *   - We only trigger on genuine chunk / static-asset failures, never
 *     on unrelated runtime errors.
 */

const SELF_HEAL_FLAG = "refclick-self-healed";

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .then((reg) => {
        // If a new SW is already waiting when we load, tell it to
        // activate immediately — otherwise it'd only take over after
        // every tab was closed, which for a single-user daily tool
        // means "never".
        if (reg.waiting) reg.waiting.postMessage("SKIP_WAITING");
        reg.addEventListener("updatefound", () => {
          const next = reg.installing;
          if (!next) return;
          next.addEventListener("statechange", () => {
            if (
              next.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              next.postMessage("SKIP_WAITING");
            }
          });
        });
      })
      .catch(() => {
        /* SW registration failures are non-fatal */
      });

    // When the controller changes (new SW took over), reload once so
    // the freshly-served index.html + hashed chunks are guaranteed to
    // match. Guarded with a flag so we never bounce.
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      // Only reload if the page is currently visible — reloading a
      // background tab is user-hostile.
      if (document.visibilityState === "visible") window.location.reload();
    });

    // Once first paint has settled (idle callback or 3 s fallback),
    // ask the SW to warm the avatar pool. Delaying this keeps the
    // initial app-shell fetches from competing with 50+ avatar
    // requests over a slow connection — the user sees pixels first,
    // then the offline cache fills in the background.
    const kickWarm = () => {
      try {
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage("WARM_AVATARS");
        }
      } catch {
        /* controller may not be ready yet — ok */
      }
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(kickWarm, { timeout: 3000 });
    } else {
      window.setTimeout(kickWarm, 3000);
    }
  });

  armChunkLoadSelfHeal();
}

/** Attach global error handlers that recover from stale hashed
 *  chunks: purge all caches, drop the SW registration, then reload
 *  once. This is the last line of defence against the "black screen"
 *  scenario — even if the SW never updated (older Safari bugs, corp
 *  proxies, whatever) the next visit self-heals. */
function armChunkLoadSelfHeal() {
  const trigger = (why) => {
    if (sessionStorage.getItem(SELF_HEAL_FLAG)) return; // one-shot
    sessionStorage.setItem(SELF_HEAL_FLAG, why || "chunk");
    // Best-effort purge — don't await, we want to reload quickly.
    Promise.resolve()
      .then(async () => {
        try {
          if ("caches" in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
          }
          if (navigator.serviceWorker) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
          }
        } catch {
          /* swallow — we're about to reload anyway */
        }
      })
      .finally(() => window.location.reload());
  };

  const looksLikeChunkError = (msg) =>
    typeof msg === "string" &&
    /chunkloaderror|loading chunk|failed to fetch dynamically imported module|importing a module script failed/i.test(
      msg,
    );

  window.addEventListener("error", (e) => {
    // Static-asset load failure (missing hashed .js/.css) surfaces
    // as an error event on the <script>/<link> element itself.
    const t = e.target;
    if (
      t &&
      (t.tagName === "SCRIPT" || t.tagName === "LINK") &&
      t.src !== undefined
    ) {
      const url = t.src || t.href || "";
      if (url && url.startsWith(window.location.origin)) {
        trigger(`asset:${url}`);
        return;
      }
    }
    if (e && (looksLikeChunkError(e.message) || looksLikeChunkError(String(e.error)))) {
      trigger("error");
    }
  }, true);

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e && e.reason;
    const msg = reason && (reason.message || String(reason));
    if (looksLikeChunkError(msg) || (reason && reason.name === "ChunkLoadError")) {
      trigger("rejection");
    }
  });

  // Clear the flag once the app has been alive for 5s — if we got
  // this far we're clearly healthy and future chunk failures should
  // be allowed to self-heal again.
  window.setTimeout(() => sessionStorage.removeItem(SELF_HEAL_FLAG), 5000);
}
